import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Sparkles, Building2, Mail, Brain, Loader2, Mail as MailIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { billingAPI } from '../../lib/api';
import { FALLBACK_TIER_CATALOG } from '../../lib/tierCatalog';
import {
  formatPropertyLimit,
  formatNotificationLimit,
  getTierDisplayName,
  getTierExtraFeatures,
} from '../../lib/pricingDisplay';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useI18n } from '../../i18n';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

/**
 * Shared pricing grid for public /pricing and in-app /app/pricing.
 * @param {'marketing' | 'app'} variant
 */
export default function PricingPlansContent({ variant = 'marketing' }) {
  const { t } = useI18n();
  const { isAuthenticated } = useAuth();
  const { tier: contextTier, refresh: refreshSubscription } = useSubscription();
  const navigate = useNavigate();
  const isApp = variant === 'app';

  const [annual, setAnnual] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [recommended, setRecommended] = useState('pro_plus');
  const [currentTier, setCurrentTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);
  const [billingReady, setBillingReady] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const tiersRes = await billingAPI.getTiers();
        const loaded = tiersRes.data.tiers || [];
        if (loaded.length === 0) {
          setTiers(FALLBACK_TIER_CATALOG.tiers);
          setRecommended(FALLBACK_TIER_CATALOG.recommended_tier);
          setUsedFallback(true);
        } else {
          setTiers(loaded);
          setRecommended(tiersRes.data.recommended_tier || 'pro_plus');
          setUsedFallback(false);
        }
        if (isApp && contextTier) {
          setCurrentTier(contextTier);
        } else if (isAuthenticated) {
          try {
            const subRes = await billingAPI.getSubscription();
            if (subRes?.data?.tier) setCurrentTier(subRes.data.tier);
          } catch {
            /* keep default */
          }
        }
        if (isAuthenticated) {
          try {
            const statusRes = await billingAPI.getStatus();
            setBillingReady(Boolean(statusRes.data?.ready_for_checkout));
          } catch {
            setBillingReady(false);
          }
        }
      } catch {
        setTiers(FALLBACK_TIER_CATALOG.tiers);
        setRecommended(FALLBACK_TIER_CATALOG.recommended_tier);
        setUsedFallback(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, isApp, contextTier]);

  const startCheckout = async (slug) => {
    if (slug === 'enterprise') {
      window.location.href = 'mailto:info@domio.world?subject=Domio%20Enterprise';
      return;
    }
    if (!isAuthenticated) {
      navigate(`/register?plan=${slug}`);
      return;
    }
    if (billingReady === false) {
      toast.error(t('pricing.stripeNotConfigured'));
      return;
    }
    setCheckoutTier(slug);
    try {
      const res = await billingAPI.createCheckout({
        tier: slug,
        billing_cycle: annual ? 'annual' : 'monthly',
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const detailStr = typeof detail === 'string' ? detail : '';
      const msg =
        err.response?.status === 503 && detailStr && /stripe/i.test(detailStr)
          ? detailStr
          : detailStr && /not configured|STRIPE_PRICE/i.test(detailStr)
            ? t('pricing.stripeNotConfigured')
            : detailStr || t('pricing.checkoutUnavailable');
      toast.error(msg);
    } finally {
      setCheckoutTier(null);
    }
  };

  if (loading) {
    return (
      <div className={cn('flex items-center justify-center', isApp ? 'py-24' : 'min-h-[40vh] pt-8')}>
        <Loader2 className="h-8 w-8 animate-spin text-domio-600" />
      </div>
    );
  }

  const billingToggle = (
    <div
      className={cn(
        'inline-flex items-center gap-2 p-1 rounded-lg',
        isApp ? 'bg-slate-100 border border-slate-200' : 'bg-white/10 backdrop-blur-sm border border-white/20',
      )}
    >
      <button
        type="button"
        className={cn(
          'px-4 py-2 rounded-md text-sm font-medium transition-colors',
          !annual
            ? isApp
              ? 'bg-white text-slate-900 shadow'
              : 'bg-white text-slate-900 shadow'
            : isApp
              ? 'text-slate-600 hover:text-slate-900'
              : 'text-white/90 hover:text-white',
        )}
        onClick={() => setAnnual(false)}
      >
        {t('pricing.monthly')}
      </button>
      <button
        type="button"
        className={cn(
          'px-4 py-2 rounded-md text-sm font-medium transition-colors',
          annual
            ? isApp
              ? 'bg-white text-slate-900 shadow'
              : 'bg-white text-slate-900 shadow'
            : isApp
              ? 'text-slate-600 hover:text-slate-900'
              : 'text-white/90 hover:text-white',
        )}
        onClick={() => setAnnual(true)}
      >
        {t('pricing.annual')}{' '}
        <span className={cn('text-xs ml-1', isApp ? 'text-domio-green' : 'text-domio-green')}>
          {t('pricing.annualSavings')}
        </span>
      </button>
    </div>
  );

  return (
    <>
      {!isApp && (
        <section className="relative pt-28 pb-16 bg-[#071E35] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#071E35] via-[#0A2F52] to-[#0D3E6B]" />
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                'radial-gradient(circle at 25% 50%, #73B83F 0%, transparent 50%), radial-gradient(circle at 75% 50%, #0D3E6B 0%, transparent 50%)',
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-domio-green mb-3">
              {t('pricing.eyebrow')}
            </p>
            <h1 className="font-manrope text-4xl sm:text-5xl font-extrabold text-white mb-4">
              {t('pricing.title')}
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
              {t('pricing.subtitle')}
            </p>
            <div className="mt-8">{billingToggle}</div>
          </div>
        </section>
      )}

      {isApp && <div className="flex justify-center sm:justify-start">{billingToggle}</div>}

      <div className={cn(isApp ? 'py-4' : 'py-16 bg-slate-50')}>
        <div className={cn(isApp ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8')}>
          {usedFallback && (
            <p className="mb-8 text-center text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg py-2 px-4">
              {t('pricing.fallbackNotice')}
            </p>
          )}
          {isAuthenticated && billingReady === false && (
            <p className="mb-8 text-center text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg py-2 px-4">
              {t('pricing.stripeNotConfigured')}
            </p>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {tiers.map((tier) => {
              const isEnterprise = tier.slug === 'enterprise';
              const isCurrent = tier.slug === currentTier;
              const isRec = tier.slug === recommended;
              const monthly = tier.price_monthly_usd;
              const annualPrice = tier.price_annual_usd;
              const displayPrice = isEnterprise
                ? t('pricing.custom')
                : annual && annualPrice != null
                  ? `$${annualPrice}`
                  : monthly === 0
                    ? '$0'
                    : `$${monthly}`;
              const extraFeatures = getTierExtraFeatures(t, tier);

              return (
                <div
                  key={tier.slug}
                  className={cn(
                    'relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm',
                    isRec && 'ring-2 ring-domio-green border-domio-green/30',
                  )}
                >
                  {isRec && (
                    <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-domio-green hover:bg-domio-green">
                      {t('pricing.recommended')}
                    </Badge>
                  )}
                  {tier.is_paid && (
                    <Badge variant="outline" className="w-fit mb-2 text-xs border-domio-green/40 text-domio-700">
                      {t('pricing.trialBadge')}
                    </Badge>
                  )}
                  <h3 className="font-manrope text-lg font-bold text-slate-900">
                    {getTierDisplayName(t, tier)}
                  </h3>
                  <p className="mt-2 text-3xl font-bold text-domio-700">
                    {displayPrice}
                    {!isEnterprise && monthly > 0 && (
                      <span className="text-sm font-normal text-slate-500">
                        {annual ? t('pricing.perYear') : t('pricing.perMonth')}
                      </span>
                    )}
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                    <li className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-domio-green shrink-0" />
                      {formatPropertyLimit(t, tier.max_properties)}
                    </li>
                    <li className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-domio-green shrink-0" />
                      {formatNotificationLimit(t, tier.max_monthly_notifications)}
                    </li>
                    {tier.ai_enabled && (
                      <li className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-violet-500 shrink-0" />
                        {t('pricing.aiFeature')}
                      </li>
                    )}
                    {extraFeatures.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-domio-green shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Button disabled className="mt-6 w-full" variant="outline">
                      {t('pricing.currentPlan')}
                    </Button>
                  ) : isEnterprise ? (
                    <Button className="mt-6 w-full" variant="outline" onClick={() => startCheckout('enterprise')}>
                      {t('pricing.contactUs')}
                    </Button>
                  ) : tier.is_paid ? (
                    <Button
                      className="mt-6 w-full bg-domio-green hover:bg-domio-green-dark text-white"
                      disabled={checkoutTier === tier.slug}
                      onClick={async () => {
                        await startCheckout(tier.slug);
                        if (isApp) refreshSubscription();
                      }}
                    >
                      {checkoutTier === tier.slug ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          {t('pricing.startTrial')}
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      asChild
                      className="mt-6 w-full border-domio-green text-domio-700 hover:bg-domio-50"
                      variant="outline"
                    >
                      <Link to="/register">{t('pricing.getStartedFree')}</Link>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {!isApp && (
        <section className="py-16 bg-gradient-to-br from-domio-600 to-[#071E35]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('pricing.ctaTitle')}
            </h2>
            <p className="text-slate-300 mb-6">{t('pricing.ctaSubtitle')}</p>
            <a
              href="mailto:info@domio.world"
              className="inline-flex items-center gap-2 text-white font-semibold hover:text-domio-green transition"
            >
              <MailIcon className="h-5 w-5" /> info@domio.world
            </a>
          </div>
        </section>
      )}
    </>
  );
}
